// Named exports remain stable while intrinsic tag strings avoid an extra React
// component boundary for every rendered portfolio element.
export const EditA = 'a';
export const EditArticle = 'article';
export const EditAside = 'aside';
export const EditAudio = 'audio';
export const EditBlockquote = 'blockquote';
export const EditBr = 'br';
export const EditCaption = 'caption';
export const EditCode = 'code';
export const EditCol = 'col';
export const EditColgroup = 'colgroup';
export const EditDetails = 'details';
export const EditDiv = 'div';
export const EditEm = 'em';
export const EditFigcaption = 'figcaption';
export const EditFigure = 'figure';
export const EditFooter = 'footer';
export const EditH1 = 'h1';
export const EditH2 = 'h2';
export const EditH3 = 'h3';
export const EditH4 = 'h4';
export const EditH5 = 'h5';
export const EditH6 = 'h6';
export const EditHeader = 'header';
export const EditHr = 'hr';
export const EditI = 'i';
export const EditIframe = 'iframe';
export const EditImg = 'img';
export const EditLi = 'li';
export const EditMain = 'main';
export const EditMark = 'mark';
export const EditNav = 'nav';
export const EditOl = 'ol';
export const EditP = 'p';
export const EditPicture = 'picture';
export const EditPre = 'pre';
export const EditProgress = 'progress';
export const EditProjects = 'projects';
export const EditSection = 'section';
export const EditSkills = 'skills';
export const EditSmall = 'small';
export const EditSource = 'source';
export const EditSpan = 'span';
export const EditStrong = 'strong';
export const EditSummary = 'summary';
export const EditTable = 'table';
export const EditTbody = 'tbody';
export const EditTd = 'td';
export const EditTfoot = 'tfoot';
export const EditTh = 'th';
export const EditThead = 'thead';
export const EditTime = 'time';
export const EditTr = 'tr';
export const EditUl = 'ul';
export const EditVideo = 'video';

// This explicit registry is the final rendering allowlist for saved and AI-generated nodes.
export const PORTFOLIO_ELEMENT_REGISTRY = Object.freeze({
  a: EditA,
  article: EditArticle,
  aside: EditAside,
  audio: EditAudio,
  blockquote: EditBlockquote,
  br: EditBr,
  caption: EditCaption,
  code: EditCode,
  col: EditCol,
  colgroup: EditColgroup,
  details: EditDetails,
  div: EditDiv,
  em: EditEm,
  figcaption: EditFigcaption,
  figure: EditFigure,
  footer: EditFooter,
  h1: EditH1,
  h2: EditH2,
  h3: EditH3,
  h4: EditH4,
  h5: EditH5,
  h6: EditH6,
  header: EditHeader,
  hr: EditHr,
  i: EditI,
  iframe: EditIframe,
  img: EditImg,
  li: EditLi,
  main: EditMain,
  mark: EditMark,
  nav: EditNav,
  ol: EditOl,
  p: EditP,
  picture: EditPicture,
  pre: EditPre,
  progress: EditProgress,
  projects: EditProjects,
  section: EditSection,
  skills: EditSkills,
  small: EditSmall,
  source: EditSource,
  span: EditSpan,
  strong: EditStrong,
  summary: EditSummary,
  table: EditTable,
  tbody: EditTbody,
  td: EditTd,
  tfoot: EditTfoot,
  th: EditTh,
  thead: EditThead,
  time: EditTime,
  tr: EditTr,
  ul: EditUl,
  video: EditVideo,
});

export const PORTFOLIO_TAGS = new Set(Object.keys(PORTFOLIO_ELEMENT_REGISTRY));

// Only structural elements expose child-element controls and central drop zones.
export const PORTFOLIO_CHILD_CONTAINER_TAGS = new Set([
  'article',
  'aside',
  'blockquote',
  'details',
  'div',
  'figure',
  'footer',
  'header',
  'li',
  'main',
  'nav',
  'ol',
  'picture',
  'projects',
  'section',
  'skills',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
]);

export const canPortfolioElementContainElements = (tagName) => (
  PORTFOLIO_CHILD_CONTAINER_TAGS.has(String(tagName || '').toLowerCase())
);

const REQUIRED_PARENT_TAGS = Object.freeze({
  caption: new Set(['table']),
  col: new Set(['colgroup', 'table']),
  colgroup: new Set(['table']),
  figcaption: new Set(['figure']),
  li: new Set(['ol', 'ul']),
  source: new Set(['audio', 'picture', 'video']),
  summary: new Set(['details']),
  tbody: new Set(['table']),
  td: new Set(['tr']),
  tfoot: new Set(['table']),
  th: new Set(['tr']),
  thead: new Set(['table']),
  tr: new Set(['table', 'tbody', 'tfoot', 'thead']),
});

const RESTRICTED_PARENT_CHILDREN = Object.freeze({
  colgroup: new Set(['col']),
  ol: new Set(['li']),
  picture: new Set(['img', 'source']),
  projects: new Set(['div']),
  skills: new Set(['div']),
  table: new Set(['caption', 'col', 'colgroup', 'tbody', 'tfoot', 'thead', 'tr']),
  tbody: new Set(['tr']),
  tfoot: new Set(['tr']),
  thead: new Set(['tr']),
  tr: new Set(['td', 'th']),
  ul: new Set(['li']),
});

const ELEMENTS_WITHOUT_CONTENT = new Set([
  'br', 'col', 'hr', 'iframe', 'img', 'source',
]);

const PHRASING_CONTENT_TAGS = new Set([
  'a', 'audio', 'br', 'code', 'em', 'i', 'iframe', 'img', 'mark', 'progress',
  'small', 'span', 'strong', 'time', 'video',
]);

const PHRASING_ONLY_PARENTS = new Set([
  'a', 'code', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'i', 'mark', 'p',
  'small', 'span', 'strong', 'summary', 'time',
]);

const TEXT_RESTRICTED_PARENTS = new Set([
  'colgroup', 'ol', 'picture', 'projects', 'skills', 'table', 'tbody', 'tfoot',
  'thead', 'tr', 'ul',
]);

/** Checks semantic HTML nesting without imposing editor drag-target restrictions. */
export const canPortfolioElementNestChild = (parentTagName, childTagName) => {
  const parentTag = String(parentTagName || '').toLowerCase();
  const childTag = String(childTagName || '').toLowerCase();

  if (
    !PORTFOLIO_TAGS.has(parentTag)
    || !PORTFOLIO_TAGS.has(childTag)
    || ELEMENTS_WITHOUT_CONTENT.has(parentTag)
  ) {
    return false;
  }

  const requiredParents = REQUIRED_PARENT_TAGS[childTag];
  if (requiredParents && !requiredParents.has(parentTag)) return false;

  const allowedChildren = RESTRICTED_PARENT_CHILDREN[parentTag];
  if (allowedChildren) return allowedChildren.has(childTag);

  if (PHRASING_ONLY_PARENTS.has(parentTag)) {
    return PHRASING_CONTENT_TAGS.has(childTag)
      && !(parentTag === 'a' && childTag === 'a');
  }

  if (parentTag === 'audio' || parentTag === 'video') {
    return childTag === 'source' || PHRASING_CONTENT_TAGS.has(childTag);
  }

  return true;
};

export const canPortfolioElementNestText = (parentTagName) => {
  const parentTag = String(parentTagName || '').toLowerCase();
  return PORTFOLIO_TAGS.has(parentTag)
    && !ELEMENTS_WITHOUT_CONTENT.has(parentTag)
    && !TEXT_RESTRICTED_PARENTS.has(parentTag);
};

/** Returns whether a registered portfolio element can safely contain another element. */
export const canPortfolioElementAcceptChild = (parentTagName, childTagName) => {
  const parentTag = String(parentTagName || '').toLowerCase();
  const childTag = String(childTagName || '').toLowerCase();

  if (!PORTFOLIO_TAGS.has(childTag) || !canPortfolioElementContainElements(parentTag)) {
    return false;
  }

  const requiredParents = REQUIRED_PARENT_TAGS[childTag];
  if (requiredParents && !requiredParents.has(parentTag)) {
    return false;
  }

  const allowedChildren = RESTRICTED_PARENT_CHILDREN[parentTag];
  return !allowedChildren || allowedChildren.has(childTag);
};

export const getPortfolioEditElement = (tagName) => (
  PORTFOLIO_ELEMENT_REGISTRY[String(tagName || '').toLowerCase()] || EditDiv
);
